import {createFileRoute} from '@tanstack/react-router'
import {useEffect, useRef} from 'react'
import {config} from '@/config/config.ts'
import LandingNav from '../landing/components/LandingNav.tsx'
import FooterSection from '../landing/sections/FooterSection.tsx'

export const Route = createFileRoute('/for-players')({
    component: RouteComponent,
})

// Scoped stylesheet for the player marketing page (tokens prefixed --sp-). Colours mirror the
// site: royal blue primary, navy bands, gold reserved for the coin system. The page's own nav
// and footer were dropped in favour of the real LandingNav / FooterSection.
const CSS = `
.sp-page{
  --sp-royal:#1D4ED8; --sp-royal-bright:#2E6BFF; --sp-royal-soft:#EAF0FE;
  --sp-navy:#0A1A33; --sp-navy-2:#10254A;
  --sp-gold:#FFC42E; --sp-gold-deep:#C8920A; --sp-gold-soft:#FFF6DF;
  --sp-bg:#FFFFFF; --sp-bg-soft:#F5F8FC; --sp-ink:#12203A; --sp-muted:#5A6B85;
  --sp-faint:#8A97AC; --sp-line:#E4EAF3;
  --sp-ui-card:#12293F; --sp-ui-card-hi:#16324C; --sp-ui-edge:rgba(90,150,200,.25);
  --sp-ui-text:#EAF1FB; --sp-ui-muted:#93A8C4; --sp-ok:#35D07F;
  --sp-radius:14px;
  background:var(--sp-bg); color:var(--sp-ink); font-size:16px; line-height:1.6;
  -webkit-font-smoothing:antialiased;
}
.sp-page ::selection{background:var(--sp-gold);color:#221904}
/* Anchor colour comes from Tailwind preflight (inherit at zero specificity) — a scoped
   .sp-page a rule would out-specify the nav's text-white utility and grey out its links. */
.sp-page a{text-decoration:none}
.sp-page img,.sp-page svg{display:block}
.sp-wrap{max-width:1160px;margin:0 auto;padding:0 24px}
.sp-eyebrow{font-family:var(--font-bebas, 'Barlow Condensed', sans-serif);font-weight:600;letter-spacing:.22em;text-transform:uppercase;font-size:13px;color:var(--sp-royal)}
.sp-page h1,.sp-page h2{font-family:var(--font-bebas, 'Barlow Condensed', sans-serif);font-weight:700;line-height:1.04;letter-spacing:.005em}
.sp-page h1{font-size:clamp(42px,6vw,72px)}
.sp-page h2{font-size:clamp(30px,4vw,46px)}
.sp-page h3{font-family:var(--font-bebas, 'Barlow Condensed', sans-serif);font-weight:600;font-size:22px;letter-spacing:.01em}
.sp-lede{color:var(--sp-muted);font-size:17px;max-width:56ch}
.sp-num{font-variant-numeric:tabular-nums}
.sp-btn{display:inline-flex;align-items:center;gap:10px;font-weight:600;font-size:15.5px;padding:13px 26px;border-radius:10px;border:1px solid transparent;transition:transform .15s ease,box-shadow .15s ease,background .15s ease;cursor:pointer}
.sp-btn:focus-visible{outline:3px solid var(--sp-royal-bright);outline-offset:3px}
.sp-btn-primary{background:var(--sp-royal);color:#fff;box-shadow:0 6px 20px rgba(29,78,216,.25)}
.sp-btn-primary:hover{background:var(--sp-royal-bright);transform:translateY(-2px);box-shadow:0 10px 26px rgba(29,78,216,.32)}
.sp-btn-outline{border-color:var(--sp-line);color:var(--sp-ink);background:#fff}
.sp-btn-outline:hover{border-color:var(--sp-royal);color:var(--sp-royal)}
.sp-btn-gold{background:var(--sp-gold);color:#221904;box-shadow:0 6px 20px rgba(255,196,46,.35)}
.sp-btn-gold:hover{transform:translateY(-2px);box-shadow:0 10px 26px rgba(255,196,46,.45)}
.sp-cta-row{display:flex;justify-content:center;align-items:center;gap:16px;flex-wrap:wrap;margin-top:46px}
.sp-cta-row .sp-hint{font-size:13.5px;color:var(--sp-faint);width:100%;text-align:center;margin-top:2px}
.sp-coin{width:1em;height:1em;flex:none}

/* hero — lives inside the site's navy gradient band, under the real LandingNav */
.sp-hero{position:relative;overflow:hidden;padding:56px 0 96px;color:#fff}
.sp-hero-grid{display:grid;grid-template-columns:1.05fr .95fr;gap:64px;align-items:center}
@media(max-width:940px){.sp-hero-grid{grid-template-columns:1fr;gap:48px}}
.sp-hero .sp-eyebrow{color:var(--sp-gold)}
.sp-hero h1{color:#fff}
.sp-hero h1 .gold{color:var(--sp-gold)}
.sp-hero .sp-lede{color:#B9C7DE;margin:20px 0 30px}
.sp-hero-ctas{display:flex;gap:14px;flex-wrap:wrap}
.sp-hero .sp-btn-outline{border-color:rgba(255,255,255,.3);color:#fff;background:transparent}
.sp-hero .sp-btn-outline:hover{border-color:var(--sp-gold);color:var(--sp-gold)}
.sp-hero-note{margin-top:18px;font-size:13.5px;color:#8FA3C2}
.sp-hero-note b{color:#C7D3E8;font-weight:600}

/* match-day ledger */
.sp-ledger{background:linear-gradient(180deg,var(--sp-ui-card-hi),var(--sp-ui-card));border:1px solid var(--sp-ui-edge);border-radius:18px;box-shadow:0 30px 70px rgba(10,26,51,.30),inset 0 1px 0 rgba(255,255,255,.06);padding:26px 26px 24px;max-width:460px;margin-left:auto;color:var(--sp-ui-text)}
@media(max-width:940px){.sp-ledger{margin-left:0}}
.sp-ledger-head{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px}
.sp-ledger-head .day{font-weight:600;font-size:15px;letter-spacing:.14em;text-transform:uppercase;color:var(--sp-ui-muted)}
.sp-live{display:inline-flex;align-items:center;gap:7px;font-size:11.5px;font-weight:600;letter-spacing:.12em;color:var(--sp-ok)}
.sp-live i{width:7px;height:7px;border-radius:50%;background:var(--sp-ok);animation:sp-pulse 1.6s infinite}
@keyframes sp-pulse{0%,100%{opacity:1}50%{opacity:.35}}
.sp-ledger-total{display:flex;align-items:center;gap:12px;margin:4px 0 18px}
.sp-ledger-total .sp-coin{width:34px;height:34px}
.sp-ledger-total .val{font-weight:700;font-size:58px;line-height:1;color:var(--sp-gold)}
.sp-ledger-total .cap{font-size:12.5px;color:var(--sp-ui-muted);line-height:1.35}
.sp-ledger-rows{display:flex;flex-direction:column;gap:9px;min-height:212px}
.sp-lrow{display:flex;align-items:center;gap:12px;background:rgba(10,20,35,.55);border:1px solid rgba(90,150,200,.14);border-radius:10px;padding:10px 14px;opacity:0;transform:translateY(10px);transition:opacity .45s ease,transform .45s ease}
.sp-lrow.on{opacity:1;transform:none}
.sp-lrow .plus{font-weight:700;font-size:19px;color:var(--sp-gold);min-width:46px;display:flex;align-items:center;gap:5px}
.sp-lrow .what{font-size:14px;font-weight:600}
.sp-lrow .sub{font-size:12px;color:#64789A;margin-left:auto;text-align:right}
.sp-tierbar{margin-top:20px}
.sp-tierbar .lbl{display:flex;justify-content:space-between;font-size:12.5px;color:var(--sp-ui-muted);margin-bottom:8px}
.sp-tierbar .lbl b{color:var(--sp-gold);font-weight:700;letter-spacing:.06em}
.sp-track{height:9px;border-radius:99px;background:rgba(10,20,35,.7);border:1px solid rgba(90,150,200,.14);overflow:hidden}
.sp-fill{height:100%;width:0;border-radius:99px;background:linear-gradient(90deg,var(--sp-gold-deep),var(--sp-gold));transition:width 1.4s cubic-bezier(.2,.7,.2,1)}

/* sections */
.sp-section{padding:96px 0}
.sp-section.soft{background:var(--sp-bg-soft);border-top:1px solid var(--sp-line);border-bottom:1px solid var(--sp-line)}
.sp-sec-head{max-width:640px;margin-bottom:52px}
.sp-sec-head .sp-eyebrow{display:block;margin-bottom:12px}
.sp-sec-head h2{color:var(--sp-navy)}
.sp-sec-head p{margin-top:16px}
.sp-reveal{opacity:0;transform:translateY(22px);transition:opacity .6s ease,transform .6s ease}
.sp-reveal.on{opacity:1;transform:none}

/* earn grid */
.sp-earn-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
@media(max-width:980px){.sp-earn-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:540px){.sp-earn-grid{grid-template-columns:1fr}}
.sp-earn-card{background:#fff;border:1px solid var(--sp-line);border-radius:var(--sp-radius);padding:24px 20px 20px;position:relative;overflow:hidden;box-shadow:0 2px 10px rgba(18,32,58,.04);transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease}
.sp-earn-card:hover{transform:translateY(-4px);border-color:var(--sp-gold);box-shadow:0 14px 30px rgba(18,32,58,.08)}
.sp-earn-card .val{display:flex;align-items:center;gap:8px;font-weight:700;font-size:38px;color:var(--sp-gold-deep);line-height:1}
.sp-earn-card .val .sp-coin{width:22px;height:22px}
.sp-earn-card h3{margin:10px 0 4px;font-size:19px;color:var(--sp-navy)}
.sp-earn-card p{font-size:13.5px;color:var(--sp-muted);line-height:1.5}
.sp-earn-card .tag{position:absolute;top:16px;right:16px;font-size:10.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--sp-royal);background:var(--sp-royal-soft);border:1px solid rgba(29,78,216,.22);padding:3px 8px;border-radius:99px}
.sp-earn-foot{margin-top:26px;font-size:13px;color:var(--sp-faint)}

/* tiers */
.sp-tier-row{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}
@media(max-width:900px){.sp-tier-row{grid-template-columns:repeat(2,1fr)}}
@media(max-width:540px){.sp-tier-row{grid-template-columns:1fr}}
.sp-tier{background:#fff;border:1px solid var(--sp-line);border-radius:var(--sp-radius);padding:26px 22px;text-align:center;position:relative;box-shadow:0 2px 10px rgba(18,32,58,.04)}
.sp-tier .medal{width:58px;height:58px;margin:0 auto 14px}
.sp-tier h3{font-size:24px;color:var(--sp-navy)}
.sp-tier .thresh{font-weight:600;font-size:16px;color:var(--sp-muted);letter-spacing:.05em;margin:2px 0 14px}
.sp-tier ul{list-style:none;font-size:13.5px;color:var(--sp-muted);display:flex;flex-direction:column;gap:6px;padding:0;margin:0}
.sp-tier ul li::before{content:"•";color:var(--sp-gold-deep);margin-right:7px}
.sp-tier.gold{border-color:var(--sp-gold);background:linear-gradient(180deg,var(--sp-gold-soft),#fff 60%);box-shadow:0 18px 40px rgba(255,196,46,.18)}
.sp-tier .you{position:absolute;top:-11px;left:50%;transform:translateX(-50%);font-weight:700;font-size:12px;letter-spacing:.14em;text-transform:uppercase;background:var(--sp-gold);color:#221904;padding:3px 12px;border-radius:99px;white-space:nowrap}

/* features */
.sp-feat-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
@media(max-width:940px){.sp-feat-grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:560px){.sp-feat-grid{grid-template-columns:1fr}}
.sp-feat{background:#fff;border:1px solid var(--sp-line);border-radius:var(--sp-radius);padding:26px 22px;box-shadow:0 2px 10px rgba(18,32,58,.04);transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease}
.sp-feat:hover{transform:translateY(-4px);border-color:var(--sp-royal);box-shadow:0 14px 30px rgba(18,32,58,.08)}
.sp-feat .ico{width:44px;height:44px;border-radius:11px;display:grid;place-items:center;margin-bottom:16px;background:var(--sp-royal-soft);border:1px solid rgba(29,78,216,.18);color:var(--sp-royal)}
.sp-feat h3{font-size:20px;margin-bottom:6px;color:var(--sp-navy)}
.sp-feat p{font-size:14px;color:var(--sp-muted)}
.sp-feat .earnline{margin-top:12px;font-size:12.5px;font-weight:600;color:var(--sp-gold-deep);display:flex;align-items:center;gap:6px}
.sp-feat .earnline .sp-coin{width:13px;height:13px}
.sp-feat .morelink{margin-top:12px;display:inline-block;font-size:13px;font-weight:600;color:var(--sp-royal)}
.sp-feat .morelink:hover{text-decoration:underline}

/* navy stats band */
.sp-band{background:radial-gradient(700px 380px at 85% -20%,rgba(46,107,255,.25),transparent 60%),var(--sp-navy);color:var(--sp-ui-text);padding:96px 0}
.sp-band .sp-eyebrow{color:var(--sp-gold)}
.sp-band h2{color:#fff}
.sp-band .sp-lede{color:var(--sp-ui-muted)}
.sp-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;text-align:center}
@media(max-width:820px){.sp-stats{grid-template-columns:repeat(2,1fr)}}
.sp-stat{padding:26px 12px;border:1px solid rgba(255,255,255,.10);border-radius:var(--sp-radius);background:rgba(255,255,255,.04)}
.sp-stat .n{font-weight:700;font-size:52px;color:var(--sp-gold);line-height:1}
.sp-stat .l{font-size:13px;color:var(--sp-ui-muted);margin-top:6px;letter-spacing:.04em}

/* rules */
.sp-rules{background:#fff;border:1px solid var(--sp-line);border-radius:var(--sp-radius);padding:30px;display:grid;grid-template-columns:220px 1fr;gap:30px;box-shadow:0 2px 10px rgba(18,32,58,.04)}
@media(max-width:760px){.sp-rules{grid-template-columns:1fr;gap:16px}}
.sp-rules h3{color:var(--sp-navy)}
.sp-rules ul{list-style:none;columns:2;column-gap:36px;font-size:13.5px;color:var(--sp-muted);padding:0;margin:0}
@media(max-width:760px){.sp-rules ul{columns:1}}
.sp-rules li{margin-bottom:9px;break-inside:avoid;padding-left:16px;position:relative}
.sp-rules li::before{content:"";position:absolute;left:0;top:8px;width:6px;height:6px;border-radius:2px;background:var(--sp-gold)}

/* final CTA */
.sp-final{text-align:center;padding:110px 24px;position:relative;overflow:hidden;color:#fff;background:radial-gradient(700px 380px at 50% 130%,rgba(255,196,46,.16),transparent 60%),radial-gradient(800px 420px at 50% -30%,rgba(46,107,255,.30),transparent 60%),var(--sp-navy)}
.sp-final h2{max-width:760px;margin:0 auto 18px;color:#fff}
.sp-final p{color:var(--sp-ui-muted);max-width:52ch;margin:0 auto 32px}
.sp-final .gold{color:var(--sp-gold)}

@media (prefers-reduced-motion: reduce){
  .sp-page *,.sp-page *::before,.sp-page *::after{animation:none!important;transition:none!important}
  .sp-reveal,.sp-lrow{opacity:1;transform:none}
  .sp-fill{width:72%!important}
}
`;

const Coin = ({className = 'sp-coin'}: {className?: string}) => (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true"><use href="#sp-coinSym" /></svg>
);

const Medal = () => (
    <svg className="medal" viewBox="0 0 64 64"><use href="#sp-medalSym" /></svg>
);

function RouteComponent() {
    const pageRef = useRef<HTMLDivElement | null>(null);

    // Scroll reveals, ledger sequence and count-ups — a straight port of the source page's
    // script, scoped to this page and cleaned up on unmount.
    useEffect(() => {
        const root = pageRef.current;
        if (!root) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const timeouts: number[] = [];
        const observers: IntersectionObserver[] = [];

        const countUp = (el: HTMLElement, to: number, dur: number) => {
            if (reduced) { el.textContent = to.toLocaleString(); return; }
            let start: number | null = null;
            const step = (ts: number) => {
                if (!start) start = ts;
                const p = Math.min((ts - start) / dur, 1);
                const eased = 1 - Math.pow(1 - p, 3);
                el.textContent = Math.round(to * eased).toLocaleString();
                if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        };

        const revealEls = root.querySelectorAll('.sp-reveal');
        if (reduced || !('IntersectionObserver' in window)) {
            revealEls.forEach((el) => el.classList.add('on'));
        } else {
            const ro = new IntersectionObserver((entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) { e.target.classList.add('on'); ro.unobserve(e.target); }
                });
            }, {threshold: .15});
            revealEls.forEach((el) => ro.observe(el));
            observers.push(ro);
        }

        const rows = Array.from(root.querySelectorAll<HTMLElement>('.sp-lrow'));
        const counter = root.querySelector<HTMLElement>('#sp-coinCount');
        const fill = root.querySelector<HTMLElement>('#sp-tierFill');
        let running = 0;
        if (reduced) {
            let total = 0;
            rows.forEach((r) => { r.classList.add('on'); total += parseInt(r.dataset.add || '0', 10); });
            if (counter) counter.textContent = String(total);
            if (fill) fill.style.width = '72%';
        } else {
            rows.forEach((r, i) => {
                timeouts.push(window.setTimeout(() => {
                    r.classList.add('on');
                    running += parseInt(r.dataset.add || '0', 10);
                    if (counter) countUp(counter, running, 500);
                }, 700 + i * 850));
            });
            timeouts.push(window.setTimeout(() => { if (fill) fill.style.width = '72%'; }, 700 + rows.length * 850 + 300));
        }

        const stats = root.querySelectorAll<HTMLElement>('.sp-stat .n');
        if (reduced || !('IntersectionObserver' in window)) {
            stats.forEach((s) => { s.textContent = parseInt(s.dataset.count || '0', 10).toLocaleString(); });
        } else {
            const so = new IntersectionObserver((entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        countUp(e.target as HTMLElement, parseInt((e.target as HTMLElement).dataset.count || '0', 10), 1400);
                        so.unobserve(e.target);
                    }
                });
            }, {threshold: .4});
            stats.forEach((s) => so.observe(s));
            observers.push(so);
        }

        return () => {
            timeouts.forEach(clearTimeout);
            observers.forEach((o) => o.disconnect());
        };
    }, []);

    return (
        <div className="sp-page" ref={pageRef}>
            <title>ScorePal for Players — Every Match Earns Coins</title>
            <meta name="description" content="Play ranked padel, pickleball, tennis, badminton or squash on ScorePal and earn coins for every match, win, streak and upset. Climb the tiers, track your rating, and see your name on the League of Players." />
            <link rel="canonical" href={`${config.publicUrl}/for-players/`} />
            <style>{CSS}</style>

            {/* coin + medal symbols */}
            <svg width="0" height="0" style={{position: 'absolute'}}>
                <defs>
                    <g id="sp-coinSym">
                        <circle cx="12" cy="12" r="11" fill="#FFC42E" />
                        <circle cx="12" cy="12" r="11" fill="none" stroke="#B37E06" strokeWidth="1.4" />
                        <circle cx="12" cy="12" r="7.4" fill="none" stroke="#B37E06" strokeWidth="1.2" />
                        <path d="M12 8.2l1.1 2.3 2.5.35-1.8 1.75.45 2.5L12 13.9l-2.25 1.2.45-2.5-1.8-1.75 2.5-.35z" fill="#B37E06" />
                    </g>
                    <g id="sp-medalSym">
                        <path d="M20 4h24l6 12-18 24L14 16z" fill="currentColor" opacity=".9" />
                        <circle cx="32" cy="40" r="16" fill="currentColor" />
                        <circle cx="32" cy="40" r="11" fill="#FFFFFF" opacity=".3" />
                        <path d="M32 33.5l2 4.1 4.5.65-3.25 3.15.8 4.5L32 43.8l-4.05 2.1.8-4.5-3.25-3.15 4.5-.65z" fill="#FFFFFF" opacity=".65" />
                    </g>
                </defs>
            </svg>

            {/* ===== Site header + hero — the homepage's exact rounded gradient card ===== */}
            <section className="relative overflow-hidden bg-[#EDEEF4] p-4">
                <div className="relative rounded-4xl overflow-hidden bg-gradient-to-br from-[#0B3DB0] via-[#0A37A0] to-[#06246E]">
                    {/* Soft top glow so the headline lifts off the blue (same as the homepage) */}
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.14),transparent_60%)]" />
                    <div className="relative z-10">
                        <LandingNav />

                        <header className="sp-hero">
                    <div className="sp-wrap sp-hero-grid">
                        <div>
                            <span className="sp-eyebrow">ScorePal for players</span>
                            <h1>Every match you play <span className="gold">pays you back</span></h1>
                            <p className="sp-lede">Play ranked padel, pickleball, tennis, badminton or squash on ScorePal and every match, win, streak and upset earns coins toward your tier. Your rating, your history, your rewards — all on one profile.</p>
                            <div className="sp-hero-ctas">
                                <a className="sp-btn sp-btn-primary" href="/signup/">Get started - it's free</a>
                                <a className="sp-btn sp-btn-outline" href="#earn">See how coins work</a>
                            </div>
                            <p className="sp-hero-note"><b>Free for players.</b> Works with leagues, tournaments and one-off ranked matches. Official DUPR partner.</p>
                        </div>

                        {/* signature: match-day ledger */}
                        <div className="sp-ledger" aria-label="Example of coins earned in one match day">
                            <div className="sp-ledger-head">
                                <span className="day">Saturday · Match day</span>
                                <span className="sp-live"><i></i>LIVE</span>
                            </div>
                            <div className="sp-ledger-total">
                                <Coin />
                                <span className="val sp-num" id="sp-coinCount">0</span>
                                <span className="cap">coins earned<br />today</span>
                            </div>
                            <div className="sp-ledger-rows">
                                <div className="sp-lrow" data-add="10"><span className="plus">+10 <Coin /></span><span className="what">Played a match</span><span className="sub">League · Court 3</span></div>
                                <div className="sp-lrow" data-add="10"><span className="plus">+10 <Coin /></span><span className="what">Won the match</span><span className="sub">6–4 · 7–5</span></div>
                                <div className="sp-lrow" data-add="25"><span className="plus">+25 <Coin /></span><span className="what">Beat a higher-ranked player</span><span className="sub">SPPR upset</span></div>
                                <div className="sp-lrow" data-add="15"><span className="plus">+15 <Coin /></span><span className="what">Weekly streak</span><span className="sub">3rd game this week</span></div>
                            </div>
                            <div className="sp-tierbar">
                                <div className="lbl"><span>Points tier</span><b>GOLD · <span className="sp-num">1,614</span> coins</b></div>
                                <div className="sp-track"><div className="sp-fill" id="sp-tierFill"></div></div>
                            </div>
                        </div>
                    </div>
                        </header>
                    </div>
                </div>
            </section>

            {/* ===== EARN ===== */}
            <section className="sp-section soft" id="earn">
                <div className="sp-wrap">
                    <div className="sp-sec-head sp-reveal">
                        <span className="sp-eyebrow">Earning coins</span>
                        <h2>Seven ways to stack coins</h2>
                        <p className="sp-lede">Coins come from playing, not grinding. Show up, compete, bring friends — the ledger takes care of itself.</p>
                    </div>
                    <div className="sp-earn-grid">
                        <div className="sp-earn-card sp-reveal">
                            <div className="val">+10 <Coin /></div>
                            <h3>Play a match</h3>
                            <p>Every ranked league, tournament or one-off match you complete.</p>
                        </div>
                        <div className="sp-earn-card sp-reveal">
                            <div className="val">+10 <Coin /></div>
                            <h3>Win a match</h3>
                            <p>A bonus on top of your play coins every time you take the match.</p>
                        </div>
                        <div className="sp-earn-card sp-reveal">
                            <div className="val">+15 <Coin /></div>
                            <h3>Weekly streak</h3>
                            <p>Play 3 or more matches in a single week to keep your streak alive.</p>
                        </div>
                        <div className="sp-earn-card sp-reveal">
                            <span className="tag">Biggest payout</span>
                            <div className="val">+50 <Coin /></div>
                            <h3>Invite a friend</h3>
                            <p>Earned once your invite joins ScorePal and completes 2 or more games.</p>
                        </div>
                        <div className="sp-earn-card sp-reveal">
                            <span className="tag">SPPR based</span>
                            <div className="val">+25 <Coin /></div>
                            <h3>Beat higher ranked</h3>
                            <p>Upset a player with a higher SPPR rating and get rewarded for it.</p>
                        </div>
                        <div className="sp-earn-card sp-reveal">
                            <div className="val">+10 <Coin /></div>
                            <h3>Matchmaking</h3>
                            <p>Extra coins on every game you play through the Matchmaking option.</p>
                        </div>
                        <div className="sp-earn-card sp-reveal">
                            <div className="val">+2 <Coin /></div>
                            <h3>Daily login</h3>
                            <p>Check your standings, matches and rating — 2 coins a day, every day.</p>
                        </div>
                    </div>
                    <p className="sp-earn-foot sp-reveal">Only ranked games count toward coins: League, Tournament and One-Off Ranked matches.</p>
                    <div className="sp-cta-row sp-reveal">
                        <a className="sp-btn sp-btn-primary" href="/signup/">Get started - it's free</a>
                        <span className="sp-hint">Free for players · Padel, pickleball, tennis, badminton &amp; squash</span>
                    </div>
                </div>
            </section>

            {/* ===== TIERS ===== */}
            <section className="sp-section" id="tiers">
                <div className="sp-wrap">
                    <div className="sp-sec-head sp-reveal">
                        <span className="sp-eyebrow">Points tiers</span>
                        <h2>Climb the tiers. Wear the badge.</h2>
                        <p className="sp-lede">Your lifetime coins set your tier. The badge sits on your player profile, next to your rating, for every opponent to see.</p>
                    </div>
                    <div className="sp-tier-row">
                        <div className="sp-tier sp-reveal" style={{color: '#B0784A'}}>
                            <Medal />
                            <h3>Bronze</h3>
                            <div className="thresh sp-num">0 – 499 coins</div>
                            <ul><li>Bronze profile badge</li><li>Coin ledger &amp; streak tracking</li></ul>
                        </div>
                        <div className="sp-tier sp-reveal" style={{color: '#9AA9BC'}}>
                            <Medal />
                            <h3>Silver</h3>
                            <div className="thresh sp-num">500 – 1,499 coins</div>
                            <ul><li>Silver profile badge</li><li>Leaderboard highlight</li></ul>
                        </div>
                        <div className="sp-tier gold sp-reveal" style={{color: '#E5AC13'}}>
                            <span className="you">Most players aim here</span>
                            <Medal />
                            <h3>Gold</h3>
                            <div className="thresh sp-num">1,500 – 3,999 coins</div>
                            <ul><li>Gold profile badge</li><li>Featured on club leaderboards</li></ul>
                        </div>
                        <div className="sp-tier sp-reveal" style={{color: '#5FB7E8'}}>
                            <Medal />
                            <h3>Platinum</h3>
                            <div className="thresh sp-num">4,000+ coins</div>
                            <ul><li>Platinum profile badge</li><li>Top-tier flair on TV Mode</li></ul>
                        </div>
                    </div>
                    <div className="sp-cta-row sp-reveal">
                        <a className="sp-btn sp-btn-primary" href="/signup/">Get started - it's free</a>
                        <a className="sp-btn sp-btn-outline" href="/leaderboards/players/">See the League of Players</a>
                    </div>
                </div>
            </section>

            {/* ===== FEATURES ===== */}
            <section className="sp-section soft" id="features">
                <div className="sp-wrap">
                    <div className="sp-sec-head sp-reveal">
                        <span className="sp-eyebrow">Built for players</span>
                        <h2>More than a scoreboard</h2>
                        <p className="sp-lede">Coins are the fun part. Underneath is everything you need to find matches, play them, and track how good you're getting.</p>
                    </div>
                    <div className="sp-feat-grid">
                        <div className="sp-feat sp-reveal">
                            <div className="ico"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 17l6-6 4 4 8-8" /><path d="M21 7v6h-6" /></svg></div>
                            <h3>SPPR rating + DUPR sync</h3>
                            <p>Every ranked match moves your ScorePal rating, and your results connect straight to DUPR.</p>
                            <span className="earnline"><Coin />+25 for beating a higher SPPR</span>
                        </div>
                        <div className="sp-feat sp-reveal">
                            <div className="ico"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="14" rx="2" /><path d="M8 22h8M12 18v4" /></svg></div>
                            <h3>Live scoring &amp; TV Mode</h3>
                            <p>Follow every court in real time. Standings, brackets and scores update the moment they're entered.</p>
                            <a className="morelink" href="/live-scoring/">How live scoring works →</a>
                        </div>
                        <div className="sp-feat sp-reveal">
                            <div className="ico"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20c.6-3.2 2.9-5 5.5-5s4.9 1.8 5.5 5" /><path d="M17 8h5M19.5 5.5v5" /></svg></div>
                            <h3>Matchmaking</h3>
                            <p>Get paired with players at your level whenever you want a game — no group-chat wrangling.</p>
                            <span className="earnline"><Coin />+10 extra per matchmade game</span>
                        </div>
                        <div className="sp-feat sp-reveal">
                            <div className="ico"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.5" /><path d="M4.5 21c.8-3.8 3.7-6 7.5-6s6.7 2.2 7.5 6" /></svg></div>
                            <h3>Player profile</h3>
                            <p>Games played, wins, upsets, streaks and coins — your whole match history in one place, forever.</p>
                        </div>
                        <div className="sp-feat sp-reveal">
                            <div className="ico"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg></div>
                            <h3>Register &amp; pay in-app</h3>
                            <p>Join leagues and tournaments in a couple of taps, with entry fees handled securely on ScorePal.</p>
                            <a className="morelink" href="/find-tournaments/">Find tournaments near you →</a>
                        </div>
                        <div className="sp-feat sp-reveal">
                            <div className="ico"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3c1 3-2 4-2 7a4 4 0 008 0c0-1-.5-2-1-3 2 1 4 3.5 4 7a7 7 0 11-14 0c0-4 3-6.5 5-11z" /></svg></div>
                            <h3>Streaks &amp; milestones</h3>
                            <p>Weekly streaks keep you coming back; milestone totals show how far you've come each season.</p>
                            <span className="earnline"><Coin />+15 per weekly streak</span>
                        </div>
                    </div>
                    <div className="sp-cta-row sp-reveal">
                        <a className="sp-btn sp-btn-primary" href="/signup/">Get started - it's free</a>
                    </div>
                </div>
            </section>

            {/* ===== STATS BAND ===== */}
            <section className="sp-band" id="proof">
                <div className="sp-wrap">
                    <div className="sp-sec-head sp-reveal">
                        <span className="sp-eyebrow">Your season, tracked</span>
                        <h2>The numbers follow you</h2>
                        <p className="sp-lede">A season on ScorePal builds a record you can point to. Here's what one Gold-tier season looks like.</p>
                    </div>
                    <div className="sp-stats sp-reveal">
                        <div className="sp-stat"><div className="n sp-num" data-count="55">0</div><div className="l">Games played</div></div>
                        <div className="sp-stat"><div className="n sp-num" data-count="24">0</div><div className="l">Games won</div></div>
                        <div className="sp-stat"><div className="n sp-num" data-count="6">0</div><div className="l">Upset wins</div></div>
                        <div className="sp-stat"><div className="n sp-num" data-count="1614">0</div><div className="l">Coins banked</div></div>
                    </div>
                    <div className="sp-cta-row sp-reveal">
                        <a className="sp-btn sp-btn-gold" href="/signup/"><Coin />Start your record</a>
                    </div>
                </div>
            </section>

            {/* ===== RULES ===== */}
            <section className="sp-section" id="fairplay">
                <div className="sp-wrap">
                    <div className="sp-rules sp-reveal">
                        <div>
                            <span className="sp-eyebrow" style={{display: 'block', marginBottom: 10}}>Fair play</span>
                            <h3>How coins stay honest</h3>
                        </div>
                        <ul>
                            <li>Only ranked games count — League, Tournament and One-Off Ranked matches.</li>
                            <li>A streak means 3+ matches within a single week.</li>
                            <li>Beating a higher-ranked player (by SPPR rating) earns the 25-coin bonus.</li>
                            <li>Invite coins land once your friend completes 2 or more games.</li>
                            <li>Matchmaking coins apply only to games played through Matchmaking.</li>
                            <li>Redemptions are verified on submission, and gameplay patterns are monitored.</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* ===== FINAL CTA ===== */}
            <section className="sp-final" id="join">
                <span className="sp-eyebrow" style={{display: 'block', marginBottom: 14, color: 'var(--sp-gold)'}}>Free player profile</span>
                <h2>Your next match is worth <span className="gold">+10</span> — minimum</h2>
                <p>Create your free ScorePal profile, join a league or tournament near you, and start your ledger with your very first ranked game.</p>
                <a className="sp-btn sp-btn-gold" href="/signup/"><Coin />Get started - it's free</a>
            </section>

            <FooterSection />
        </div>
    );
}
